/* import __COLOCATED_TEMPLATE__ from './stripe-component.hbs'; */
/* RESPONSIBLE TEAM: team-billing */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, keepLatestTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import ENV from 'embercom/config/environment';
import { post } from 'embercom/lib/ajax';
import Countries from 'embercom/lib/countries';

import type Router from '@ember/routing/router-service';
import { type TaskGenerator } from 'ember-concurrency';
import { captureException } from 'embercom/lib/sentry';

import type Store from '@ember-data/store';
import type StripeIntent from 'embercom/models/billing/stripe-intent';
import type ThemeService from 'embercom/services/theme-service';
import nonEuTaxEligibleCountries from 'embercom/lib/non-eu-tax-eligible-countries';

export interface AfterPaymentMethodSuccess {
  paymentMethod: string;
  stripeIntent?: StripeIntent;
  address: {
    countryCode: string;
    streetAddress: string;
    stateCode?: string;
    postCode?: string;
    city?: string;
  };
  taxNumber?: string;
  useExistingPaymentMethod?: boolean;
}

export enum INTENT_TYPES {
  paymentIntent = 'payment_intent',
  setupIntent = 'setup_intent',
}

interface Args {
  redirectUrl: string;
  amount: number;
  type: INTENT_TYPES;
  immediatelyMountStripeElements: boolean;
  enableExistingPaymentMethod?: boolean;
  additionalIsProcessingDisplayStrings?: string[];
  afterSuccessTask: (afterSuccessPayload: AfterPaymentMethodSuccess) => TaskGenerator<void>;
}

interface AddressResultFromStripeElements {
  city?: string;
  country?: string;
  line1?: string;
  postal_code?: string;
  state?: string;
}

interface UpdateShippingAddressResults {
  city?: string;
  country?: string;
  line1?: string;
  postal_code?: string;
  state?: string;
  vat_number?: string;
}

interface StripeElement {
  mount: (selector: string) => void;
}

enum addressValidationCountryCodes {
  unitedStates = 'US',
  canada = 'CA',
}

interface Signature {
  Element: any;
  Args: Args;
  Blocks: {
    default: [
      {
        paymentElement: any;
        confirmButton: any;
      },
    ];
  };
}

export default class StripeComponent extends Component<Signature> {
  @service declare appService: AppService;
  @service declare customerService: any;
  @service declare intl: any;
  @service declare stripev3: any;
  @service declare notificationsService: any;
  @service declare router: Router;
  @service declare themeService: ThemeService;

  @service declare store: Store;
  @service declare purchaseAnalyticsService: any;

  @tracked elements: any;
  @tracked isProcessing = false;
  @tracked startCyclingText = false;
  @tracked useExistingCard = false;
  @tracked paymentElementReference!: StripeElement;
  @tracked addressElementReference!: StripeElement;

  @tracked stripeIntent?: StripeIntent;
  @tracked amount = 100;
  @tracked enableExistingPaymentMethod = false;
  @tracked displayStrings: string[] = this.args.additionalIsProcessingDisplayStrings ?? [];

  // billing address attributes
  @tracked countryCode?: string;
  @tracked stateCode?: string;
  @tracked postCode?: string;
  @tracked city?: string;
  @tracked streetAddress?: string;
  @tracked vatNumber?: string;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.enableExistingPaymentMethod = this.args.enableExistingPaymentMethod ?? false;
    if (!this.args.immediatelyMountStripeElements) {
      taskFor(this.init).perform();
    }
    if (this.customerVatNumber) {
      this.vatNumber = this.customerVatNumber;
    }
  }

  @keepLatestTask
  *init() {
    yield taskFor(this.configureStripeElements).perform();
    if (!this.cardHasExpired) {
      this.useExistingCard = this.supportExistingPaymentMethod;
    } else {
      this.useExistingCard = false;
    }
    this.resetDisplayString();
  }

  @action
  resetDisplayString() {
    this.displayStrings = this.args.additionalIsProcessingDisplayStrings ?? [
      this.intl.t('app.components.loading-button.checkout.processing-payment'),
      this.intl.t('app.components.loading-button.checkout.creating-subscription'),
      this.intl.t('app.components.loading-button.checkout.activating-subscription'),
    ];
  }

  get supportExistingPaymentMethod() {
    return (
      (this.hasDefaultPaymentMethod &&
        this.hasCardAsDefaultPaymentMethod &&
        this.stripeIntent?.hasExistingPaymentMethod &&
        this.enableExistingPaymentMethod) ||
      false
    );
  }

  get isMountingElements() {
    return taskFor(this.mountStripeElements).isRunning && !this.isProcessing;
  }

  get showVatField() {
    let country = Countries.allCountries.find(
      (country) => country.code.toUpperCase() === this.countryCode?.toUpperCase(),
    );

    // Show VAT field for EU countries
    if (country?.inEu) {
      return true;
    }

    // Show VAT field for additional countries
    if (this.countryCode) {
      return nonEuTaxEligibleCountries.includes(this.countryCode.toUpperCase());
    }

    return false;
  }

  get hasDefaultPaymentMethod() {
    return this.customerService.customer.hasDefaultPaymentMethod;
  }

  get hasCardAsDefaultPaymentMethod() {
    return this.customerService.customer.hasCardAsDefaultPaymentMethod;
  }

  get existingCard() {
    return this.customerService.customer.existingCardDetails;
  }

  get cardHasExpired() {
    return (
      this.customerService.customer.cardExpiryDate &&
      this.customerService.customer.cardExpiryDate < new Date()
    );
  }

  get formattedCardExpiryMonth() {
    return this.customerService.customer.formattedCardExpiresAt;
  }

  get customerVatNumber() {
    return this.customerService.customer.vatNumber;
  }

  @action
  setUseExistingCard(value: boolean) {
    if (this.cardHasExpired && value === true) {
      this.notificationsService.notifyError(this.intl.t('billing.stripe-component.card-expired'));
      this.useExistingCard = false;
      return;
    }
    this.useExistingCard = value;
  }

  private addressRequiresValidationForCountry(countryCode: string) {
    if (this.appService.app.disableFrontendAddressValidation) {
      return false;
    }

    let countryCodesRequiringChecks: string[] = Object.values(addressValidationCountryCodes);
    return countryCodesRequiringChecks.includes(countryCode);
  }

  @action
  setAddress(addressResult: AddressResultFromStripeElements) {
    this.countryCode = addressResult.country;
    this.stateCode = addressResult?.state ? addressResult?.state : undefined;
    this.postCode = addressResult?.postal_code ? addressResult?.postal_code : undefined;
    this.city = addressResult?.city ? addressResult.city : undefined;
    this.streetAddress = addressResult.line1;
  }

  @action
  setVatNumber(event: Event & { target: HTMLInputElement }) {
    this.vatNumber = event.target.value;
  }

  @keepLatestTask
  *mountStripeElements({ forceReload }: { forceReload: boolean }): TaskGenerator<void> {
    if (!(this.paymentElementReference && this.addressElementReference) || forceReload) {
      yield taskFor(this.configureStripeElements).perform();
    }

    try {
      this.paymentElementReference.mount('#payment-element');
      this.addressElementReference.mount('#address-element');
      this.elements.getElement('address').on('change', (event: any) => {
        let countryCode = event.value.address?.country;
        if (countryCode) {
          this.countryCode = countryCode;
        }
      });
    } catch (err) {
      captureException(err, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      this.notificationsService.notifyError(
        this.intl.t('signup.teams.pricing5.annual-plans.stripe.payment-element-error'),
      );
      console.error(err);
    }
  }

  @keepLatestTask
  *configureStripeElements(): TaskGenerator<void> {
    try {
      let stripeIntent = yield taskFor(this.createPaymentIntent).perform(this.amount);
      let key = this.getStripeKey();

      if (this.stripev3.stripeOptions?.stripeAccount !== stripeIntent.stripeAccountId) {
        this.stripev3._stripe = null;
      }
      yield this.stripev3.load(key, { stripeAccount: stripeIntent.stripeAccountId });

      let getComputedCssVar = (varName: string) =>
        window.getComputedStyle(document.documentElement).getPropertyValue(varName);
      let options = {
        loader: 'auto',
        appearance: {
          theme: 'stripe',
          variables: {
            colorText: getComputedCssVar('--text-default'),
            colorPrimary: getComputedCssVar('--accent-fill'),
            colorBackground: getComputedCssVar('--base-module'),
          },
        },
        clientSecret: stripeIntent.clientSecret,
      };

      this.elements = this.stripev3.elements(options);

      let addressOptions = { mode: 'billing', fields: { name: 'never' } };

      let paymentElementOptions = {
        terms: {
          card: 'never',
          googlePay: 'never',
          paypal: 'never',
          applePay: 'never',
        },
        layout: 'tabs',
      };

      this.addressElementReference = this.elements.create('address', addressOptions);
      this.paymentElementReference = this.elements.create('payment', paymentElementOptions);
    } catch (err) {
      let errorMessage =
        err.jqXHR?.responseJSON?.[0] ??
        this.intl.t('signup.teams.pricing5.annual-plans.stripe.payment-element-error');

      this.notificationsService.notifyError(errorMessage);
      captureException(err, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      console.error(err);
    }
  }

  @action
  setIsProcessing(value: boolean) {
    if (this.isProcessing !== value) {
      this.isProcessing = value;
    }
  }

  @action
  setIsCyclingText(value: boolean) {
    if (this.startCyclingText !== value) {
      this.startCyclingText = value;
    }
  }

  @action
  setIsProcessingAndSetStartCyclingText(value: boolean) {
    this.setIsProcessing(value);
    this.setIsCyclingText(value);
  }

  @action
  addToLoadingButtonDisplayStringsAndStartCyclingText(text: string) {
    this.displayStrings = [text, ...this.displayStrings];
    this.setIsProcessingAndSetStartCyclingText(true);
  }

  @action
  resetLoadingButtonDisplayStrings() {
    this.resetDisplayString();
    this.setIsProcessingAndSetStartCyclingText(false);
  }

  @dropTask
  *confirmPayment(): TaskGenerator<void> {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: 'confirm_payment',
      place: 'stripe-payment-method',
      context: {
        payment_intent: this.stripeIntent?.isPaymentIntent,
        app_id: this.appService.app.id,
        support_existing_payment_method: this.supportExistingPaymentMethod,
        use_existing_payment_method: this.useExistingCard,
      },
    });
    if (!this.useExistingCard) {
      let addressElement = this.elements.getElement('address');

      let addressResult = yield addressElement.getValue();
      this.setAddress(addressResult.value.address);
      if (this.addressRequiresValidationForCountry(this.countryCode ?? '')) {
        try {
          this.addToLoadingButtonDisplayStringsAndStartCyclingText(
            this.intl.t('app.components.loading-button.checkout.validating-billing-address'),
          );
          yield taskFor(this.validateAddress).perform();
        } catch (err) {
          this.purchaseAnalyticsService.trackEvent({
            action: 'errored',
            object: 'pay_now_button',
            context: 'address_validation',
            place: 'stripe-payment-method',
          });
          this.resetLoadingButtonDisplayStrings();
          return;
        }
      }
    }

    if (this.vatNumber && (!this.useExistingCard || this.vatNumber !== this.customerVatNumber)) {
      try {
        this.addToLoadingButtonDisplayStringsAndStartCyclingText(
          this.intl.t('app.components.loading-button.checkout.validating-vat-number'),
        );
        yield taskFor(this.validateTaxNumber).perform();
      } catch (err) {
        this.purchaseAnalyticsService.trackEvent({
          action: 'errored',
          object: 'pay_now_button',
          context: 'vat_number_validation',
          place: 'stripe-payment-method',
        });
        this.resetLoadingButtonDisplayStrings();
        return;
      }
    }

    if (this.args.amount !== this.stripeIntent?.amount && this.stripeIntent?.isPaymentIntent) {
      try {
        yield taskFor(this.updatePaymentIntent).perform(this.args.amount);
      } catch (err) {
        this.notificationsService.notifyError(
          this.intl.t('signup.teams.pricing5.annual-plans.stripe.payment-element-error'),
        );
        captureException(err, {
          tags: {
            responsibleTeam: 'team-billing',
            responsible_team: 'team-billing',
          },
        });
        this.resetLoadingButtonDisplayStrings();
        console.error(err);
        return;
      }
    }

    let response;
    try {
      this.setIsProcessing(true);
      response = yield taskFor(this.confirmSetup).perform({
        elements: this.elements,
        url: this.args.redirectUrl,
      });
      this.setIsProcessingAndSetStartCyclingText(true);
    } catch (err) {
      captureException(err, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      this.notificationsService.notifyError(
        this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
      );
      console.error(err);
      this.resetLoadingButtonDisplayStrings();
      return;
    }
    //This code will never be hit if the payment method requires a redirect
    if (response.error) {
      this.handleError(response.error);
      captureException(response.error, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      this.purchaseAnalyticsService.trackEvent({
        action: 'errored',
        object: 'confirm_payment',
        context: {
          error_type: response.error?.type,
          decline_code: response.error?.decline_code || response.error?.code,
          error_code: response.error?.code,
          support_existing_payment_method: this.supportExistingPaymentMethod,
          use_existing_payment_method: this.useExistingCard,
        },
        place: 'stripe-payment-method',
      });
      this.resetLoadingButtonDisplayStrings();
      return;
    }
    let paymentMethod;
    if (this.stripeIntent!.isPaymentIntent) {
      paymentMethod = response.paymentIntent.payment_method;
    } else {
      paymentMethod = response.setupIntent.payment_method;
    }
    let afterSuccessPayload: AfterPaymentMethodSuccess = {
      paymentMethod,
      stripeIntent: this.stripeIntent,
      address: {
        countryCode: this.countryCode!,
        streetAddress: this.streetAddress!,
        stateCode: this.stateCode,
        postCode: this.postCode,
        city: this.city,
      },
      taxNumber: this.vatNumber,
      useExistingPaymentMethod: this.useExistingCard,
    };
    try {
      this.setIsProcessingAndSetStartCyclingText(true);
      yield taskFor(this.args.afterSuccessTask).perform(afterSuccessPayload);
    } catch (error) {
      yield taskFor(this.mountStripeElements).perform({ forceReload: true });
      this.resetLoadingButtonDisplayStrings();
    }
  }

  @keepLatestTask
  *confirmSetup({ elements, url }: { elements: any; url: string }): TaskGenerator<any> {
    if (!this.stripeIntent) {
      throw new Error('Error encountered with configuring payment gateway');
    }

    yield this.stripev3.load(this.getStripeKey(), {
      stripeAccount: this.stripeIntent.stripeAccountId,
    });

    let params: any = {
      redirect: 'if_required',
      confirmParams: {
        return_url: url,
      },
    };
    if (this.useExistingCard) {
      params.clientSecret = this.stripeIntent.clientSecret;
    } else {
      params.elements = elements;
    }
    let response;
    if (this.stripeIntent.isPaymentIntent) {
      response = yield this.stripev3.instance.confirmPayment(params);
    } else {
      response = yield this.stripev3.instance.confirmSetup(params);
    }
    return response;
  }

  @keepLatestTask
  *createPaymentIntent(amount_in_cent: number): TaskGenerator<StripeIntent> {
    let forceSetupIntent = this.args.type === INTENT_TYPES.setupIntent;
    let forcePaymentIntent = this.args.type === INTENT_TYPES.paymentIntent;
    let params: { [key: string]: any } = {
      app_id: this.appService.app.id,
      ...(forcePaymentIntent && {
        amount_in_cent,
      }),
      ...(forceSetupIntent && {
        force_setup_intent: true,
      }),
    };

    try {
      let response = yield post('/ember/customers/create_stripe_intent_for_checkout', params);
      this.store.pushPayload('billing/stripe-intent', {
        'billing/stripe-intent': [response],
      });
      this.stripeIntent = this.store.peekRecord('billing/stripe-intent', response.object_id)!;
      return this.stripeIntent;
    } catch (err) {
      captureException(err);
      throw err;
    }
  }

  @keepLatestTask
  *updatePaymentIntent(newAmount: number): TaskGenerator<StripeIntent> {
    if (!this.stripeIntent) {
      throw new Error('Error encountered with configuring payment gateway');
    }

    if (!this.stripeIntent.isPaymentIntent) {
      return this.stripeIntent;
    }

    let params = {
      payment_intent_id: this.stripeIntent.id,
      amount_in_cent: newAmount,
      app_id: this.appService.app.id,
    };
    try {
      let response = yield post('/ember/customers/update_payment_intent', params);
      this.stripeIntent.updateFromPayload(response);
      return this.stripeIntent;
    } catch (err) {
      captureException(err);
      throw err;
    }
  }

  @keepLatestTask
  *validateAddress(): TaskGenerator<AddressResultFromStripeElements> {
    let params = {
      country_code: this.countryCode,
      state_code: this.stateCode,
      postcode: this.postCode,
      city: this.city,
      street_address: this.streetAddress,
      app_id: this.appService.app.id,
    };
    let response;
    try {
      response = yield post('/ember/customers/validate_address', params);
      return response;
    } catch (err) {
      captureException(err);
      this.notificationsService.notifyError(
        this.intl.t('signup.teams.pricing5.annual-plans.address-error'),
      );
      console.error(err?.jqXHR?.responseJSON?.error);
      captureException(err, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      throw err;
    }
  }

  @keepLatestTask
  *validateTaxNumber(): TaskGenerator<UpdateShippingAddressResults> {
    let params = {
      country_code: this.countryCode,
      tax_number: this.vatNumber,
      app_id: this.appService.app.id,
    };
    let response;
    try {
      response = yield post(`/ember/customers/validate_tax_number`, params);
      return response;
    } catch (err) {
      captureException(err);
      let errorMessage =
        err?.jqXHR?.status === 404
          ? this.intl.t('signup.teams.pricing5.annual-plans.vat-invalid-error')
          : this.intl.t('signup.teams.pricing5.annual-plans.vat-check-error');
      let messageDurationMs = 15000; // error message for VAT check error is pretty long - need a longer display duration to read it
      this.notificationsService.notifyError(errorMessage, messageDurationMs);
      console.error(err?.jqXHR?.responseJSON?.error);
      captureException(err, {
        tags: {
          responsibleTeam: 'team-billing',
          responsible_team: 'team-billing',
        },
      });
      throw err;
    }
  }

  private handleError(error: any) {
    switch (error?.type) {
      case 'card_error':
      case 'validation_error':
        if (error.decline_code === 'lost_card' || error.decline_code === 'stolen_card') {
          this.notificationsService.notifyError(
            this.intl.t('signup.teams.pricing5.annual-plans.stripe.lost-stolen-card'),
          );
        } else {
          this.notificationsService.notifyError(error.message);
        }
        break;
      case 'invalid_request_error':
        if (error?.code === 'setup_intent_authentication_failure') {
          this.notificationsService.notifyError(
            this.intl.t('signup.teams.pricing5.annual-plans.stripe.card-unauthorized'),
          );
        } else {
          this.notificationsService.notifyError(
            this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
          );
        }
        break;
      default:
        this.notificationsService.notifyError(
          this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
        );
        break;
    }
  }

  private _isStaging() {
    return window.location.hostname.startsWith(ENV.APP.stagingHostname);
  }

  private getStripeKey() {
    if (this._isStaging()) {
      return ENV.APP.stripe.sandboxPublishableKey;
    }
    return this.stripev3.publishableKey;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Stripe::StripeComponent': typeof StripeComponent;
  }
}
